<template>
    <div class="flex flex-col xl:flex-row gap-4 xl:gap-10 text-xs text-grey-90"
         v-if="footerDateValue || showOrgInfoName || ifMarketFooter || isProject">

        <div v-if="footerDateValue" class="flex items-center gap-1 shrink-0">
            <span v-if="showLabel">{{ footerDateLabel }}:</span>
            {{ footerDateValue }}
        </div>

        <div class="flex flex-row items-center justify-between text-xs w-full gap-2" v-if="isProject">
            <div class="flex flex-row items-center gap-1 text-eureka-500 bg-blue-40 rounded px-1 py-0.5 cursor-pointer" @click="handleProjectClick">
                <PtIcon icon="SolidFolderClosed" class="w-4 h-4 text-xl fill-eureka-500 shrink-0" />
                <div>{{ profile.PROJECT_NAME }}</div>
            </div>
            <div>{{ profile.APD }}</div>
        </div>

        <!-- Owner -->
        <div class="flex items-center gap-1" v-if="showOrgInfoName">
            <span v-if="showLabel" class="shrink-0">{{ t('solutionCard.owner') }}:</span>

            <SolutionOrgIcon :org-info-logo="OrgInfo?.logo" class="h-4 w-4 shrink-0" />

            <div class="grow flex items-center gap-2">
                <a v-if="companyHref" :href="companyHref" target="blank" class="hover:underline line-clamp-1" :title="showOrgInfoName" v-safe-html="showOrgInfoName"></a>
                <span v-else :title="showOrgInfoName" v-safe-html="showOrgInfoName" />
                <OrgPopover v-if="showOrgInfoCounts" :counts="showOrgInfoCounts" :orgs="profile.ORG_INFO"></OrgPopover>
            </div>
        </div>

        <div v-if="ifMarketFooter" class="flex text-xs flex-wrap gap-2">
            <div v-if="cagr" class="grow flex items-center gap-1 mr-10">
                <span v-if="showLabel" class="shrink-0">{{ t('solutionCard.cagr') }}:</span>
                <span>{{ cagr }}</span>
                <span v-if="cagr && marketInfos" class="mx-1">|</span>
                <span v-if="marketInfos">{{ marketInfos }}</span>
            </div>
            <div v-if="category" class="grow flex items-center gap-1 mr-10">
                <span v-if="showLabel" class="shrink-0">{{ t('solutionCard.category') }}:</span>
                <a :href="categoryLink"
                   class="cursor-pointer text-eureka-500"
                   target="_blank"
                   v-safe-html="category.name"
                />
            </div>
            <div v-if="publisher || publishedTime" class="grow flex items-center gap-2">
                <span v-if="publishedTime">{{ publishedTime }}</span>
                <span v-if="publishedTime && publisher">/</span>
                <span v-if="publisher">{{ publisher }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ISolution } from './types';
import { SOLUTION_TYPE } from './types';
import { generateCompanyHref } from '@eureka/utils/company';
import { formatNumberByKilo2 } from '@eureka/utils/number-format';
import { Site, SLD } from '@patsnap-utils/biz';
import _ from 'lodash';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { cn, en, jp, tw } from './locales';
import OrgPopover from './OrgPopover.vue';
import SolutionOrgIcon from './SolutionOrgIcon.vue';
import { PtIcon } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/FolderClosed.svg';

const { t } = useI18n({
    messages: {
        cn, en, jp, tw,
    },
})

const props = withDefaults(defineProps<{
    profile: ISolution
    footerField?: string
    showLabel?: boolean
}>(), {
    showLabel: true,
})

const currentDateField = computed(() => {
    if (props.footerField) {
        return props.footerField
    }
    if (props.profile.SOLUTION_TYPE === SOLUTION_TYPE.PATENT) {
        return _.get(props.profile, 'PBD') ? 'PBD' : 'APD'
    }
    return 'PBD'
})

const footerDateLabel = computed(() => {
    return t(`solutionCard.${props.profile.SOLUTION_TYPE}.${currentDateField.value}`)
})

const isProject = computed(() => {
    return props.profile.SOLUTION_TYPE === SOLUTION_TYPE.PROJECT
})

const footerDateValue = computed(() => {
    return _.get(props.profile, currentDateField.value) || _.get(props.profile, 'publication_time')
})

const companyHref = computed(() => {
    const orgId = _.get(props.profile, 'ORG_INFO[0].id', '')
    return generateCompanyHref(orgId)
})

const showOrgInfoName = computed(() => {
    return _.get(props.profile, 'ORG_INFO[0].display_name', '')
})

const OrgInfo = computed(() => {
    return _.get(props.profile, 'ORG_INFO[0]')
})

const showOrgInfoCounts = computed(() => {
    return _.get(props.profile, 'ORG_INFO', []).length ? _.get(props.profile, 'ORG_INFO', []).length - 1 : 0
})

const cagr = computed(() => {
    const cagrAvg = _.get(props.profile, 'AVG_CAGR')
    return cagrAvg ? `${cagrAvg * 100}%` : ''
})

const category = computed(() => {
    return _.get(props.profile, 'CATEGORY[0]')
})

const publishedTime = computed(() => {
    return _.get(props.profile, 'PUBLISHED_TIME')
})

const publisher = computed(() => {
    return _.get(props.profile, 'PUBLISHER')
})

const marketInfos = computed(() => {
    const infos = _.get(props.profile, 'MARKET_INFOS') || []
    const infoRange = infos.length > 1 ? [infos[0], infos[infos.length - 1]] : infos.length === 1 ? [infos[0]] : []
    return infoRange.map(info => t('solutionCard.cagrValue', { year: info.year, mount: formatNumberByKilo2(info.normalized_market_size) })).join(' - ')
})

const ifMarketFooter = computed(() => {
    return cagr.value || category.value || publisher.value || publishedTime.value
})

const categoryLink = computed(() => {
    return `${new Site({ sld: SLD.EUREKA }).href}/discovery/#/market-category-detail/${props.profile.CATEGORY?.[0]?.id}`
})

const handleProjectClick = () => {
    window.open(new Site({ sld: SLD.EUREKA, pathname: `rd/#/graph/${props.profile.PROJECT_ID}` }).href, '_blank')
}

</script>
<style lang="scss">
.org_logo {
    width: 16px !important;
    height: 16px !important;
    border-radius: 4px;
    border: 1px solid var(--border-light);
    margin-right: 4px;
    vertical-align: text-bottom !important;
    object-fit: contain;
}
</style>
